import { Card, Col, Row } from "antd";
import { MarkdownViewer } from "components/assets/utilities/MarkdownViewer";
import SecondaryHeading from "components/assets/text/SecondaryHeading";
import Field from "redux/classes/Field";
import { useAppSelector } from "redux/hooks";
import { IOPageWrapper } from "./styles";

type FieldKind = "input" | "parameter" | "output";

const FieldSection = ({ field, kind }: { field: Field; kind: FieldKind }) => {
  // For outputs the user-facing display label lives on `fieldType` (the Authorize
  // mapper put output.label there); for inputs and parameters, `label` is correct.
  const label = kind === "output" ? field.fieldType : field.label;
  // Show field-type pill on parameters too — useful since parameters can be
  // textbox/dropdown/checkbox/etc., not just a data_type like inputs/outputs.
  const typePill = kind === "parameter"
    ? (field.fieldType || field.dataType)
    : field.dataType;

  // Required asterisk only for user-supplied fields (inputs / parameters).
  // Outputs aren't user-supplied, so no marker.
  const showRequired = (kind === "input" || kind === "parameter") && !field.optional;

  return (
    <Card className="mt-24 card-custom field-card">
      <div className="field-header">
        <h4 className="field-label">
          {label}
          {showRequired && <span className="field-required" title="Required"> *</span>}
        </h4>
        {typePill && <span className="field-data-type">{typePill}</span>}
      </div>

      {field.description && (
        <div className="card-sub-text description-text">
          <MarkdownViewer markdown={field.description} />
        </div>
      )}
    </Card>
  );
};

const IOPage = () => {
  const user = useAppSelector((state) => state.user);
  const modelInfo = user.models[user.modelIndex];

  if (!modelInfo) {
    return null;
  }

  return (
    <IOPageWrapper>
      <Row justify="center">
        <Col md={20}>
          <SecondaryHeading title="Inputs">Inputs</SecondaryHeading>
          {modelInfo.inputs.length === 0 ? (
            <p className="empty-message">This model takes no inputs.</p>
          ) : (
            modelInfo.inputs.map((input: Field) => (
              <FieldSection key={input.fieldID} field={input} kind="input" />
            ))
          )}

          <div className="section-spacer" />

          <SecondaryHeading title="Parameters">Parameters</SecondaryHeading>
          {modelInfo.parameters.length === 0 ? (
            <p className="empty-message">This model takes no parameters.</p>
          ) : (
            modelInfo.parameters.map((parameter: Field) => (
              <FieldSection key={parameter.fieldID} field={parameter} kind="parameter" />
            ))
          )}

          <div className="section-spacer" />

          <SecondaryHeading title="Outputs">Outputs</SecondaryHeading>
          {modelInfo.outputs.length === 0 ? (
            <p className="empty-message">This model produces no outputs.</p>
          ) : (
            modelInfo.outputs.map((output: Field) => (
              <FieldSection key={output.fieldID} field={output} kind="output" />
            ))
          )}
        </Col>
      </Row>
    </IOPageWrapper>
  );
};

export default IOPage;
